/*
 * «Обновления»: статус по сервису проверки версий GitLab (через бэкенд viva-gitlab) — актуальна,
 * есть обновление или обновиться срочно (критическая уязвимость); последняя версия в текущей ветке
 * и вообще; план по шагам через обязательные остановки; кнопка «Обновить до X» (шаблон «Обновление
 * GitLab платформы», исполнитель viva-ops) — только участникам группы viva и только для GitLab на
 * бутстрап-ноде; идущее обновление и история; ссылки на Upgrade Path и release notes.
 */
import { Button, Grid, Typography, makeStyles } from '@material-ui/core';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';
import {
  InfoCard,
  Link,
  Progress,
  StatusError,
  StatusOK,
  StatusPending,
  StatusWarning,
} from '@backstage/core-components';
import {
  fetchUpgradeOptions,
  fetchUpgrades,
  PLATFORM_ADMIN_GROUP,
  RELEASES_URL,
  RUN_STATE_LABELS,
  runnerLags,
  updateTarget,
  UpdateTone,
  updateView,
  upgradePathUrl,
  upgradeTemplateUrl,
} from '../../../lib/gitlab';
import { annotation } from '../../../lib/viva';
import { Field, useFieldStyles } from './Field';
import { useGitlabSummary } from './useGitlabSummary';

const useStyles = makeStyles(theme => ({
  status: { fontSize: 16, fontWeight: 700 },
  detail: { color: theme.palette.text.secondary, marginTop: theme.spacing(0.5) },
  links: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2) },
  action: { marginTop: theme.spacing(2), display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap' },
  history: { margin: 0, paddingLeft: theme.spacing(2.5) },
}));

const Tone = ({ tone, children }: { tone: UpdateTone; children: string }) => {
  switch (tone) {
    case 'ok':
      return <StatusOK>{children}</StatusOK>;
    case 'warning':
      return <StatusWarning>{children}</StatusWarning>;
    case 'error':
      return <StatusError>{children}</StatusError>;
    default:
      return <StatusPending>{children}</StatusPending>;
  }
};

const since = (iso?: string) =>
  iso
    ? new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : undefined;

/**
 * Кнопка обновления видна всегда: активна у участников viva, когда есть следующий шаг; иначе —
 * неактивна с причиной (версия последняя, нет прав, идёт обновление, GitLab не на бутстрап-ноде).
 */
const UpgradeAction = ({
  admin,
  managed,
  running,
  next,
  current,
}: {
  admin: boolean;
  managed: boolean;
  running: boolean;
  next?: string;
  current?: string;
}) => {
  const fields = useFieldStyles();
  let reason: string | undefined;
  if (!managed) {
    reason = 'GitLab не на бутстрап-ноде платформы или viva-ops не настроен — обновляет владелец GitLab';
  } else if (running) {
    reason = 'идёт обновление — ход виден выше и на главной';
  } else if (!next) {
    reason = `версия ${current ?? ''} — последняя, обновлять нечего`;
  } else if (!admin) {
    reason = 'обновлять GitLab платформы могут только участники группы viva';
  }
  return (
    <>
      <Button
        variant="contained"
        color="primary"
        disabled={Boolean(reason)}
        href={reason || !next ? undefined : upgradeTemplateUrl(next)}
      >
        {next ? `Обновить до ${next}` : 'Обновить GitLab'}
      </Button>
      <Typography className={fields.muted} variant="body2">
        {reason ?? 'проверки, бэкап, перезапуск GitLab (3–5 минут); можно сначала пробный запуск'}
      </Typography>
    </>
  );
};

export const GitlabUpdatesCard = () => {
  const classes = useStyles();
  const fields = useFieldStyles();
  const { entity } = useEntity();
  const { value, loading, error } = useGitlabSummary();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);
  const extra = useAsync(async () => {
    const apis = { discoveryApi, fetchApi };
    const [options, upgrades, identity] = await Promise.all([
      fetchUpgradeOptions(apis).catch(() => undefined),
      fetchUpgrades(apis).catch(() => undefined),
      identityApi.getBackstageIdentity(),
    ]);
    return { options, upgrades, admin: identity.ownershipEntityRefs.includes(PLATFORM_ADMIN_GROUP) };
  }, []);
  const dash = <span className={fields.muted}>—</span>;
  const managedHost = annotation(entity, 'viva.company.com/gitlab-host-role') === 'bootstrap-node';

  if (loading) {
    return (
      <InfoCard title="Обновления">
        <Progress />
      </InfoCard>
    );
  }
  if (!value) {
    return (
      <InfoCard title="Обновления">
        <Typography className={fields.muted}>
          Сводка GitLab недоступна{error ? `: ${error.message}` : ''}
        </Typography>
      </InfoCard>
    );
  }
  const view = updateView(value);
  const minor = value.version?.split('.').slice(0, 2).join('.');
  const target = updateTarget(value);
  return (
    <InfoCard title="Обновления">
      <div className={classes.status}>
        <Tone tone={view.tone}>{view.title}</Tone>
      </div>
      {view.detail && <Typography className={classes.detail}>{view.detail}</Typography>}
      <Grid container spacing={2} style={{ marginTop: 8 }}>
        <Field label="Текущая версия">
          <span className={fields.mono}>{value.version ?? '—'}</span>
        </Field>
        <Field label={minor ? `Последняя в ветке ${minor}` : 'Последняя в ветке'}>
          {value.update.latestOfMinor ? (
            <span className={fields.mono}>{value.update.latestOfMinor}</span>
          ) : (
            dash
          )}
        </Field>
        <Field label="Последняя версия GitLab">
          {value.update.latestVersion ? (
            <span className={fields.mono}>{value.update.latestVersion}</span>
          ) : (
            dash
          )}
        </Field>
        <Field label="Проверено">{since(value.update.checkedAt) ?? dash}</Field>
        <Field label="Как обновить" full>
          {target ? (
            <>
              версия образа <span className={fields.mono}>GITLAB_VERSION={target}-ce.0</span> в
              .env стенда (infra), затем deploy:stand — GitLab перезапускается 3–5 минут
            </>
          ) : (
            'версия образа — GITLAB_VERSION в .env стенда (infra), затем deploy:stand'
          )}
        </Field>
        {(extra.value?.options?.path.length ?? 0) > 1 && (
          <Field label="План обновления по шагам" full>
            <span className={fields.mono}>
              {[value.version, ...(extra.value?.options?.path ?? [])].join(' → ')}
            </span>
            <div className={fields.muted}>
              обязательные остановки GitLab — по шагу за запуск
            </div>
          </Field>
        )}
        <Field label="Ссылки" full>
          <div className={classes.links}>
            {value.version && <Link to={upgradePathUrl(value)}>Путь обновления (Upgrade Path)</Link>}
            <Link to={RELEASES_URL}>Release notes GitLab</Link>
          </div>
        </Field>
        {extra.value?.upgrades?.running && (
          <Field label="Идёт обновление" full>
            {extra.value.upgrades.running.from} → {extra.value.upgrades.running.to}
            {extra.value.upgrades.running.dryRun ? ' (пробный запуск)' : ''}, запустил{' '}
            {extra.value.upgrades.running.requestedBy}
          </Field>
        )}
        {(extra.value?.upgrades?.runners?.length ?? 0) > 0 && (
          <Field label="Раннеры" full>
            <ul className={classes.history}>
              {extra.value!.upgrades!.runners!.map(r => {
                const lagging = r.versions.some(v => runnerLags(value.version, v));
                return (
                  <li key={r.id}>
                    {r.description ?? `#${r.id}`}: <span className={fields.mono}>{r.versions.join(', ') || '—'}</span>
                    {r.status ? ` (${r.status})` : ''}
                    {lagging && (
                      <span className={fields.muted}>
                        {' '}— отстаёт от GitLab {value.version}: обновите чарт раннера кластера (gitops 20-gitlab-runner)
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </Field>
        )}
        {(extra.value?.upgrades?.history?.length ?? 0) > 0 && (
          <Field label="Последние обновления" full>
            <ul className={classes.history}>
              {extra.value!.upgrades!.history!.slice(0, 5).map(h => (
                <li key={h.id}>
                  {since(h.createdAt)}: {h.from} → {h.to}
                  {h.dryRun ? ' (пробный)' : ''} — {RUN_STATE_LABELS[h.state] ?? h.state}
                  {h.requestedBy ? `, ${h.requestedBy}` : ''}
                </li>
              ))}
            </ul>
          </Field>
        )}
      </Grid>
      {extra.value && (
        <div className={classes.action}>
          <UpgradeAction
            admin={extra.value.admin}
            managed={managedHost && Boolean(extra.value.options?.managed)}
            running={Boolean(extra.value.upgrades?.running)}
            next={extra.value.options?.next}
            current={value.version}
          />
        </div>
      )}
    </InfoCard>
  );
};
